import { useState, useEffect, useCallback } from "react";
import { 
  Loader2, Plus, RefreshCw, Trash2, Edit, Settings,
  CheckCircle, XCircle, AlertTriangle, Table2, Play, History, ExternalLink,
  ArrowRight, Zap, Eye, EyeOff, Sparkles, AlertCircle, Link2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ---- Column Mapping Row ----
function MappingRow({ column, crmFields, mappedField, isNew, isMissing, onChange }) {
  const statusColor = isMissing
    ? "border-red-300 bg-red-50"
    : mappedField && mappedField !== "__ignore__"
    ? "border-green-300 bg-green-50/50"
    : mappedField === "__ignore__"
    ? "border-slate-200 bg-slate-50 opacity-60"
    : "border-amber-300 bg-amber-50/50";

  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border ${statusColor} transition-all`} data-testid={`mapping-row-${column}`}>
      {/* Sheet Column Name */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-slate-800 truncate">{column}</span>
          {isNew && (
            <Badge className="bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0" data-testid={`new-badge-${column}`}>
              New
            </Badge>
          )}
          {isMissing && (
            <Badge className="bg-red-100 text-red-700 text-[10px] px-1.5 py-0 flex items-center gap-1" data-testid={`missing-badge-${column}`}>
              <AlertTriangle className="w-3 h-3" /> Missing
            </Badge>
          )}
        </div>
      </div>

      {/* Arrow */}
      <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />

      {/* CRM Field Dropdown */}
      <div className="flex-1">
        <Select
          value={mappedField || "_unmapped_"}
          onValueChange={(val) => onChange(column, val === "_unmapped_" ? null : val)}
          disabled={isMissing}
        >
          <SelectTrigger className="h-9 text-sm" data-testid={`mapping-select-${column}`}>
            <SelectValue placeholder="Select CRM Field..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_unmapped_">-- Not Mapped --</SelectItem>
            <SelectItem value="__ignore__">
              <span className="flex items-center gap-1.5 text-slate-500">
                <EyeOff className="w-3 h-3" /> Do Not Import
              </span>
            </SelectItem>
            {crmFields.map((f) => (
              <SelectItem key={f.field_key} value={f.field_key}>
                <span className="flex items-center gap-1.5">
                  {f.is_system ? <span className="text-indigo-500 text-xs font-medium">[Default]</span> : <span className="text-teal-600 text-xs font-medium">[Custom]</span>}
                  {f.field_label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Status Icon */}
      <div className="flex-shrink-0 w-6">
        {isMissing ? (
          <AlertCircle className="w-5 h-5 text-red-500" />
        ) : mappedField && mappedField !== "__ignore__" ? (
          <CheckCircle className="w-5 h-5 text-green-500" />
        ) : mappedField === "__ignore__" ? (
          <EyeOff className="w-5 h-5 text-slate-400" />
        ) : (
          <AlertTriangle className="w-5 h-5 text-amber-500" />
        )}
      </div>
    </div>
  );
}

// ---- Mapping Panel (shown in dialog or expanded view) ----
function MappingPanel({ integrationId, onMappingSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [autoMapping, setAutoMapping] = useState(false);
  const [mappingData, setMappingData] = useState(null);
  const [localMapping, setLocalMapping] = useState({});
  const [newColumns, setNewColumns] = useState([]);

  const fetchMapping = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/simple-sheets/${integrationId}/mapping`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setMappingData(data);
        setLocalMapping(data.column_mapping || {});
        setNewColumns([]);
      }
    } catch (e) {
      toast.error("Failed to load mapping");
    } finally {
      setLoading(false);
    }
  }, [integrationId]);

  useEffect(() => { fetchMapping(); }, [fetchMapping]);

  const handleRefreshHeaders = async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/simple-sheets/${integrationId}/refresh-headers`, {
        method: "POST",
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        if (data.status === "error") {
          toast.error(data.error);
        } else {
          setNewColumns(data.new_columns || []);
          toast.success(data.message);
          await fetchMapping();
        }
      }
    } catch (e) {
      toast.error("Failed to refresh headers");
    } finally {
      setRefreshing(false);
    }
  };

  const handleAutoMap = async () => {
    setAutoMapping(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/simple-sheets/${integrationId}/auto-map`, {
        method: "POST",
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setLocalMapping(data.final_mapping || {});
        toast.success(data.message);
      }
    } catch (e) {
      toast.error("Auto-mapping failed");
    } finally {
      setAutoMapping(false);
    }
  };

  const handleMappingChange = (column, crmField) => {
    setLocalMapping((prev) => {
      const next = { ...prev };
      if (!crmField) {
        delete next[column];
      } else {
        next[column] = crmField;
      }
      return next;
    });
  };

  const handleSaveMapping = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/simple-sheets/${integrationId}/mapping`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ column_mapping: localMapping })
      });
      if (res.ok) {
        toast.success("Mapping saved successfully");
        onMappingSaved?.();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save mapping");
      }
    } catch (e) {
      toast.error("Failed to save mapping");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
        <span className="ml-2 text-slate-500">Loading mapping...</span>
      </div>
    );
  }

  if (!mappingData) return <p className="text-center text-slate-500 py-8">No mapping data available</p>;

  const headers = mappingData.available_headers || [];
  const crmFields = mappingData.crm_fields || [];
  const mappedCount = Object.values(localMapping).filter(v => v && v !== "__ignore__").length;
  const ignoredCount = Object.values(localMapping).filter(v => v === "__ignore__").length;
  const unmappedCount = headers.length - mappedCount - ignoredCount;

  return (
    <div className="space-y-4" data-testid="mapping-panel">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <Button size="sm" variant="outline" onClick={handleRefreshHeaders} disabled={refreshing} data-testid="refresh-headers-btn">
          {refreshing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
          Refresh Fields
        </Button>
        <Button size="sm" variant="outline" onClick={handleAutoMap} disabled={autoMapping} data-testid="auto-map-btn">
          {autoMapping ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
          Smart Auto-Map
        </Button>
        <div className="flex-1" />
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 text-green-500" /> {mappedCount} Mapped</span>
          <span className="flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> {unmappedCount} Unmapped</span>
          {ignoredCount > 0 && <span className="flex items-center gap-1"><EyeOff className="w-3.5 h-3.5 text-slate-400" /> {ignoredCount} Ignored</span>}
        </div>
      </div>

      {/* New columns alert */}
      {newColumns.length > 0 && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2" data-testid="new-columns-alert">
          <Sparkles className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-blue-800">New Fields Detected!</p>
            <p className="text-xs text-blue-600 mt-0.5">
              {newColumns.length} new column{newColumns.length > 1 ? "s" : ""} found: {newColumns.join(", ")}
            </p>
          </div>
        </div>
      )}

      {/* Table Header */}
      <div className="flex items-center gap-3 px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
        <div className="flex-1">Google Sheet Column</div>
        <div className="w-4" />
        <div className="flex-1">CRM Lead Field</div>
        <div className="w-6">Status</div>
      </div>

      {/* Mapping Rows */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
        {headers.map((h) => (
          <MappingRow
            key={h}
            column={h}
            crmFields={crmFields}
            mappedField={localMapping[h] || null}
            isNew={newColumns.includes(h)}
            isMissing={false}
            onChange={handleMappingChange}
          />
        ))}
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-3 border-t">
        <Button onClick={handleSaveMapping} disabled={saving} data-testid="save-mapping-btn">
          {saving ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
          ) : (
            <><CheckCircle className="w-4 h-4 mr-2" /> Save Mapping</>
          )}
        </Button>
      </div>
    </div>
  );
}

// ---- Main Component ----
export default function GoogleSheetsIntegration({ currentUser, sheetIntegrations, onRefresh }) {
  const [sheetLoading, setSheetLoading] = useState(false);
  const [showAddSheetDialog, setShowAddSheetDialog] = useState(false);
  const [showEditSheetDialog, setShowEditSheetDialog] = useState(false);
  const [showMappingDialog, setShowMappingDialog] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [mappingIntegrationId, setMappingIntegrationId] = useState(null);
  const [sheetForm, setSheetForm] = useState({
    name: "",
    sheet_url: "",
    sheet_tab: "",
    auto_sync: false,
    sync_interval: 5
  });
  const [sheetHeaders, setSheetHeaders] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [autoSuggestions, setAutoSuggestions] = useState({});
  const [validatingUrl, setValidatingUrl] = useState(false);
  const [urlValidationResult, setUrlValidationResult] = useState(null);
  const [syncingSheetId, setSyncingSheetId] = useState(null);
  const [showSheetLogsDialog, setShowSheetLogsDialog] = useState(false);
  const [sheetLogs, setSheetLogs] = useState([]);
  const [selectedSheetIntegration, setSelectedSheetIntegration] = useState(null);
  const [availableTabs, setAvailableTabs] = useState([]);
  const [crmFields, setCrmFields] = useState([]);

  const canManageSheets = currentUser?.role === "super_admin" || currentUser?.role === "admin";
  const canDeleteSheets = currentUser?.role === "super_admin" || currentUser?.role === "admin";

  // Fetch CRM fields on mount
  useEffect(() => {
    const fetchCrmFields = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/lead-fields`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          // Use ALL fields from the Form Builder API for this business
          const fields = (Array.isArray(data) ? data : []).filter(f => f.is_active !== false && f.field_key !== "created_at");
          setCrmFields(fields);
        }
      } catch (e) { console.error("Failed to fetch CRM fields", e); }
    };
    fetchCrmFields();
  }, []);

  const handleValidateSheetUrl = async () => {
    if (!sheetForm.sheet_url) { toast.error("Please enter a Google Sheet URL"); return; }
    setValidatingUrl(true);
    setUrlValidationResult(null);
    try {
      const res = await fetch(BACKEND_URL + "/api/integrations/simple-sheets/validate-url", {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ sheet_url: sheetForm.sheet_url })
      });
      const data = await res.json();
      setUrlValidationResult(data);
      if (data.valid) {
        setSheetHeaders(data.headers || []);
        setAvailableTabs(data.tabs || [data.sheet_name || "Sheet1"]);
        setAutoSuggestions(data.auto_suggestions || {});
        // Apply auto-suggestions as initial mapping
        setColumnMapping(data.auto_suggestions || {});
        if (!sheetForm.sheet_tab && data.sheet_name) {
          setSheetForm(prev => ({ ...prev, sheet_tab: data.sheet_name }));
        }
        toast.success(`Sheet accessible! Found ${data.row_count} rows. Auto-mapped ${Object.keys(data.auto_suggestions || {}).length} columns.`);
      } else {
        toast.error(data.error || "Could not access sheet");
      }
    } catch (e) {
      setUrlValidationResult({ valid: false, error: e.message });
      toast.error("Failed to validate URL");
    } finally {
      setValidatingUrl(false);
    }
  };

  const handleCreateSimpleSheetIntegration = async () => {
    if (!sheetForm.name || !sheetForm.sheet_url) { toast.error("Please fill all required fields"); return; }
    if (!urlValidationResult?.valid) { toast.error("Please validate the Sheet URL first"); return; }
    setSheetLoading(true);
    try {
      const res = await fetch(BACKEND_URL + "/api/integrations/simple-sheets", {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: sheetForm.name, sheet_url: sheetForm.sheet_url,
          sheet_tab: sheetForm.sheet_tab || "Sheet1",
          column_mapping: columnMapping,
          auto_sync: sheetForm.auto_sync, sync_interval: sheetForm.sync_interval
        })
      });
      if (res.ok) {
        toast.success("Google Sheet integration created!");
        onRefresh();
        setShowAddSheetDialog(false);
        resetSheetForm();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to create integration");
      }
    } catch (e) { toast.error("Failed to create integration"); }
    finally { setSheetLoading(false); }
  };

  const handleUpdateSheetIntegration = async () => {
    if (!editingIntegration) return;
    setSheetLoading(true);
    try {
      const updateData = {
        name: sheetForm.name, sheet_tab: sheetForm.sheet_tab,
        auto_sync: sheetForm.auto_sync, sync_interval: sheetForm.sync_interval
      };
      if (sheetForm.sheet_url !== editingIntegration.sheet_url && urlValidationResult?.valid) {
        updateData.sheet_url = sheetForm.sheet_url;
        updateData.column_mapping = columnMapping;
      }
      const res = await fetch(`${BACKEND_URL}/api/integrations/simple-sheets/${editingIntegration.integration_id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(updateData)
      });
      if (res.ok) {
        toast.success("Integration updated!");
        onRefresh(); setShowEditSheetDialog(false); resetSheetForm();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to update");
      }
    } catch (e) { toast.error("Failed to update"); }
    finally { setSheetLoading(false); }
  };

  const handleEditIntegration = (integration) => {
    setEditingIntegration(integration);
    setSheetForm({
      name: integration.name || "", sheet_url: integration.sheet_url || "",
      sheet_tab: integration.sheet_tab || "Sheet1",
      auto_sync: integration.auto_sync || false, sync_interval: integration.sync_interval || 5
    });
    setUrlValidationResult({ valid: true });
    setSheetHeaders(Object.keys(integration.column_mapping || {}));
    setShowEditSheetDialog(true);
  };

  const handleOpenMapping = (integration) => {
    setMappingIntegrationId(integration.integration_id);
    setShowMappingDialog(true);
  };

  const handleSyncSheet = async (integrationId) => {
    setSyncingSheetId(integrationId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/simple-sheets/${integrationId}/sync`, {
        method: "POST", credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        if (data.error) { toast.error(data.error); }
        else { toast.success(`Synced: ${data.leads_created} created, ${data.leads_updated} updated, ${data.leads_skipped} skipped`); }
        onRefresh();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Sync failed");
      }
    } catch (e) { toast.error("Sync failed"); }
    finally { setSyncingSheetId(null); }
  };

  const handleDeleteSheetIntegration = async (integrationId) => {
    if (!confirm("Delete this Google Sheet integration? This action cannot be undone.")) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/simple-sheets/${integrationId}`, {
        method: "DELETE", credentials: "include"
      });
      if (res.ok) { toast.success("Integration deleted"); onRefresh(); }
      else { toast.error("Failed to delete"); }
    } catch (e) { toast.error("Failed to delete"); }
  };

  const handleViewSheetLogs = async (integration) => {
    setSelectedSheetIntegration(integration);
    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/simple-sheets/${integration.integration_id}/logs`, { credentials: "include" });
      if (res.ok) { const data = await res.json(); setSheetLogs(data.logs || []); setShowSheetLogsDialog(true); }
    } catch (e) { toast.error("Failed to load logs"); }
  };

  const handleToggleSheetAutoSync = async (integrationId, autoSync) => {
    try {
      await fetch(`${BACKEND_URL}/api/integrations/simple-sheets/${integrationId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ auto_sync: autoSync })
      });
      onRefresh();
      toast.success(autoSync ? "Auto-sync enabled" : "Auto-sync disabled");
    } catch (e) { toast.error("Failed to update"); }
  };

  const handleResetSheetSync = async (integrationId) => {
    if (!confirm("Reset sync position? This will re-import all rows on next sync.")) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/simple-sheets/${integrationId}/reset`, {
        method: "POST", credentials: "include"
      });
      if (res.ok) { toast.success("Sync position reset."); onRefresh(); }
      else { toast.error("Failed to reset"); }
    } catch (e) { toast.error("Failed to reset"); }
  };

  const resetSheetForm = () => {
    setSheetForm({ name: "", sheet_url: "", sheet_tab: "", auto_sync: false, sync_interval: 5 });
    setColumnMapping({}); setSheetHeaders([]); setAutoSuggestions({});
    setUrlValidationResult(null); setEditingIntegration(null); setAvailableTabs([]);
  };

  const handleNewMappingChange = (column, crmField) => {
    setColumnMapping(prev => {
      const next = { ...prev };
      if (!crmField) { delete next[column]; }
      else { next[column] = crmField; }
      return next;
    });
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div className="flex gap-3 items-center">
              <div className="p-2 bg-green-600 rounded-lg">
                <Table2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle>Google Sheets Integration</CardTitle>
                <CardDescription>Automatically sync leads from Google Sheets</CardDescription>
              </div>
            </div>
            {canManageSheets && (
              <Button onClick={() => setShowAddSheetDialog(true)} data-testid="add-sheet-btn">
                <Plus className="w-4 h-4 mr-2" />Add Sheet
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!canManageSheets && (
            <div className="mb-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
              <p className="text-sm text-slate-600">Contact your Admin to add or modify Google Sheets integrations.</p>
            </div>
          )}

          {sheetIntegrations.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Table2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No Google Sheets integrations</p>
              <p className="text-sm mt-1">Connect a Google Sheet to automatically sync leads</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sheetIntegrations.map(integration => (
                <div key={integration.integration_id} className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid={`sheet-integration-${integration.integration_id}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-slate-800">{integration.name}</h4>
                        {canManageSheets && (
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => handleEditIntegration(integration)}>
                            <Edit className="w-3 h-3 text-slate-500" />
                          </Button>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mt-1">
                        Tab: <strong>{integration.sheet_tab || "Sheet1"}</strong> | Headers: {(integration.available_headers || []).length} columns
                      </p>
                      <a href={integration.sheet_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 mt-1 flex items-center gap-1 hover:underline">
                        Open Sheet <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={integration.auto_sync ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}>
                        {integration.auto_sync ? "Auto-Sync ON" : "Manual"}
                      </Badge>
                      {integration.last_sync_status && (
                        <Badge className={
                          integration.last_sync_status === "success" ? "bg-green-100 text-green-700" :
                          integration.last_sync_status === "partial" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"
                        }>{integration.last_sync_status}</Badge>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="p-3 bg-white rounded-lg border">
                      <p className="text-xs text-slate-500">Total Imported</p>
                      <p className="text-lg font-bold text-green-600">{integration.total_leads_imported || 0}</p>
                    </div>
                    <div className="p-3 bg-white rounded-lg border">
                      <p className="text-xs text-slate-500">Total Updated</p>
                      <p className="text-lg font-bold text-blue-600">{integration.total_leads_updated || 0}</p>
                    </div>
                    <div className="p-3 bg-white rounded-lg border">
                      <p className="text-xs text-slate-500">Mapped Fields</p>
                      <p className="text-lg font-bold text-indigo-600">
                        {Object.values(integration.column_mapping || {}).filter(v => v && v !== "__ignore__").length}
                        <span className="text-xs text-slate-400 font-normal"> / {(integration.available_headers || []).length}</span>
                      </p>
                    </div>
                    <div className="p-3 bg-white rounded-lg border">
                      <p className="text-xs text-slate-500">Last Sync</p>
                      <p className="text-sm font-medium text-slate-700">
                        {integration.last_sync ? new Date(integration.last_sync).toLocaleString() : "Never"}
                      </p>
                    </div>
                  </div>

                  {/* Mapping Preview */}
                  {integration.column_mapping && Object.keys(integration.column_mapping).length > 0 && (
                    <div className="mb-4 p-3 bg-white rounded-lg border">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-slate-500 font-medium">Field Mapping</p>
                        {canManageSheets && (
                          <Button size="sm" variant="ghost" className="h-6 text-xs text-indigo-600" onClick={() => handleOpenMapping(integration)} data-testid={`configure-mapping-btn-${integration.integration_id}`}>
                            <Settings className="w-3 h-3 mr-1" /> Configure
                          </Button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(integration.column_mapping).map(([col, field]) => (
                          <Badge key={col} variant="outline" className={`text-xs ${field === "__ignore__" ? "text-slate-400 line-through" : "text-slate-700"}`}>
                            {col} {field === "__ignore__" ? "(ignored)" : `\u2192 ${field}`}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-2 pt-3 border-t">
                    <Button size="sm" onClick={() => handleSyncSheet(integration.integration_id)} disabled={syncingSheetId === integration.integration_id} data-testid={`sync-btn-${integration.integration_id}`}>
                      {syncingSheetId === integration.integration_id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
                      Sync Now
                    </Button>
                    {canManageSheets && (
                      <Button size="sm" variant="outline" onClick={() => handleOpenMapping(integration)} data-testid={`mapping-btn-${integration.integration_id}`}>
                        <Link2 className="w-4 h-4 mr-1" /> Mapping
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => handleViewSheetLogs(integration)}>
                      <History className="w-4 h-4 mr-1" /> Logs
                    </Button>
                    {canManageSheets && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => handleEditIntegration(integration)}>
                          <Settings className="w-4 h-4 mr-1" /> Settings
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleResetSheetSync(integration.integration_id)} title="Re-import all rows on next sync">
                          <RefreshCw className="w-4 h-4 mr-1" /> Reset
                        </Button>
                        <div className="flex items-center gap-2 ml-auto">
                          <Label className="text-xs text-slate-500">Auto-Sync</Label>
                          <Switch checked={integration.auto_sync} onCheckedChange={(v) => handleToggleSheetAutoSync(integration.integration_id, v)} />
                        </div>
                      </>
                    )}
                    {canDeleteSheets && (
                      <Button size="sm" variant="destructive" onClick={() => handleDeleteSheetIntegration(integration.integration_id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Setup Info */}
          <Card className="mt-6 bg-green-50 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium text-green-800 mb-2">Simple Setup - No Google Cloud Required!</p>
                  <ol className="text-sm text-green-700 space-y-1 list-decimal list-inside">
                    <li>Open your Google Sheet</li>
                    <li>Click <strong>Share</strong> &rarr; <strong>"Anyone with the link"</strong> &rarr; <strong>Viewer</strong></li>
                    <li>Copy the Sheet URL and click "Add Sheet"</li>
                    <li>System auto-maps columns to CRM fields</li>
                    <li>Review and adjust mapping as needed</li>
                    <li>Click Sync Now or enable Auto-Sync!</li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>
        </CardContent>
      </Card>

      {/* ---- Add Google Sheet Dialog ---- */}
      <Dialog open={showAddSheetDialog} onOpenChange={(o) => { if (!o) resetSheetForm(); setShowAddSheetDialog(o); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Google Sheet Integration</DialogTitle>
            <DialogDescription>Connect a public Google Sheet to automatically import leads</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Integration Name *</Label>
              <Input placeholder="e.g., Lead Generation Sheet" value={sheetForm.name} onChange={(e) => setSheetForm(prev => ({ ...prev, name: e.target.value }))} />
            </div>
            <div>
              <Label>Google Sheet URL *</Label>
              <div className="flex gap-2 mt-1">
                <Input placeholder="https://docs.google.com/spreadsheets/d/..." value={sheetForm.sheet_url}
                  onChange={(e) => { setSheetForm(prev => ({ ...prev, sheet_url: e.target.value })); setUrlValidationResult(null); setSheetHeaders([]); setAvailableTabs([]); setAutoSuggestions({}); }}
                  className="flex-1" />
                <Button onClick={handleValidateSheetUrl} disabled={validatingUrl || !sheetForm.sheet_url} variant="outline">
                  {validatingUrl ? <Loader2 className="w-4 h-4 animate-spin" /> : "Validate"}
                </Button>
              </div>
              {urlValidationResult && (
                <div className={`mt-2 p-3 rounded-lg ${urlValidationResult.valid ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
                  <div className="flex items-start gap-2">
                    {urlValidationResult.valid ? (
                      <><CheckCircle className="w-4 h-4 text-green-600 mt-0.5" /><span className="text-sm text-green-700">Sheet accessible! Found {urlValidationResult.row_count} rows with {urlValidationResult.headers?.length} columns.</span></>
                    ) : (
                      <><XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" /><div className="text-sm text-red-700"><p className="font-medium mb-1">Validation Failed</p><p>{urlValidationResult.error}</p></div></>
                    )}
                  </div>
                </div>
              )}
              <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800 font-medium mb-2">How to make your sheet public:</p>
                <ol className="text-xs text-amber-700 space-y-1 list-decimal list-inside">
                  <li>Open your Google Sheet &rarr; Click <strong>"Share"</strong></li>
                  <li>Change "Restricted" to <strong>"Anyone with the link"</strong> &rarr; <strong>"Viewer"</strong></li>
                </ol>
              </div>
            </div>

            {/* Tab Selection */}
            {urlValidationResult?.valid && availableTabs.length > 0 && (
              <div>
                <Label>Sheet Tab</Label>
                <Select value={sheetForm.sheet_tab || availableTabs[0]} onValueChange={(v) => setSheetForm(prev => ({ ...prev, sheet_tab: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select tab..." /></SelectTrigger>
                  <SelectContent>{availableTabs.map(tab => <SelectItem key={tab} value={tab}>{tab}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}

            {/* Dynamic Column Mapping */}
            {urlValidationResult?.valid && sheetHeaders.length > 0 && (
              <div className="p-4 bg-slate-50 rounded-lg border space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-slate-700">Column Mapping</Label>
                    <p className="text-xs text-slate-500 mt-0.5">Map sheet columns to CRM fields. Auto-mapped fields shown below.</p>
                  </div>
                  <div className="text-xs text-slate-500">
                    <CheckCircle className="w-3.5 h-3.5 text-green-500 inline mr-1" />
                    {Object.values(columnMapping).filter(v => v && v !== "__ignore__").length} mapped
                  </div>
                </div>
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                  {sheetHeaders.map(h => (
                    <MappingRow
                      key={h}
                      column={h}
                      crmFields={crmFields}
                      mappedField={columnMapping[h] || null}
                      isNew={false}
                      isMissing={false}
                      onChange={handleNewMappingChange}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Auto Sync</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Switch checked={sheetForm.auto_sync} onCheckedChange={(v) => setSheetForm(prev => ({ ...prev, auto_sync: v }))} />
                  <span className="text-sm text-slate-600">{sheetForm.auto_sync ? "Enabled" : "Disabled"}</span>
                </div>
              </div>
              <div>
                <Label>Sync Interval</Label>
                <Select value={String(sheetForm.sync_interval)} onValueChange={(v) => setSheetForm(prev => ({ ...prev, sync_interval: parseInt(v) }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Every 1 minute</SelectItem>
                    <SelectItem value="5">Every 5 minutes</SelectItem>
                    <SelectItem value="10">Every 10 minutes</SelectItem>
                    <SelectItem value="30">Every 30 minutes</SelectItem>
                    <SelectItem value="60">Every 1 hour</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => { resetSheetForm(); setShowAddSheetDialog(false); }}>Cancel</Button>
            <Button onClick={handleCreateSimpleSheetIntegration} disabled={sheetLoading || !sheetForm.name || !urlValidationResult?.valid}>
              {sheetLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : <><Plus className="w-4 h-4 mr-2" />Create Integration</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Edit Sheet Dialog ---- */}
      <Dialog open={showEditSheetDialog} onOpenChange={(o) => { if (!o) resetSheetForm(); setShowEditSheetDialog(o); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Google Sheet Integration</DialogTitle>
            <DialogDescription>Modify integration settings</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Integration Name *</Label>
              <Input value={sheetForm.name} onChange={(e) => setSheetForm(prev => ({ ...prev, name: e.target.value }))} />
            </div>
            <div>
              <Label>Google Sheet URL</Label>
              <div className="flex gap-2 mt-1">
                <Input value={sheetForm.sheet_url} onChange={(e) => {
                  setSheetForm(prev => ({ ...prev, sheet_url: e.target.value }));
                  if (e.target.value !== editingIntegration?.sheet_url) { setUrlValidationResult(null); }
                }} className="flex-1" />
                {sheetForm.sheet_url !== editingIntegration?.sheet_url && (
                  <Button onClick={handleValidateSheetUrl} disabled={validatingUrl} variant="outline">
                    {validatingUrl ? <Loader2 className="w-4 h-4 animate-spin" /> : "Validate"}
                  </Button>
                )}
              </div>
            </div>
            <div>
              <Label>Sheet Tab Name</Label>
              <Input value={sheetForm.sheet_tab} onChange={(e) => setSheetForm(prev => ({ ...prev, sheet_tab: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Auto Sync</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Switch checked={sheetForm.auto_sync} onCheckedChange={(v) => setSheetForm(prev => ({ ...prev, auto_sync: v }))} />
                  <span className="text-sm text-slate-600">{sheetForm.auto_sync ? "Enabled" : "Disabled"}</span>
                </div>
              </div>
              <div>
                <Label>Sync Interval</Label>
                <Select value={String(sheetForm.sync_interval)} onValueChange={(v) => setSheetForm(prev => ({ ...prev, sync_interval: parseInt(v) }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Every 1 minute</SelectItem>
                    <SelectItem value="5">Every 5 minutes</SelectItem>
                    <SelectItem value="10">Every 10 minutes</SelectItem>
                    <SelectItem value="30">Every 30 minutes</SelectItem>
                    <SelectItem value="60">Every 1 hour</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => { resetSheetForm(); setShowEditSheetDialog(false); }}>Cancel</Button>
            <Button onClick={handleUpdateSheetIntegration} disabled={sheetLoading || !sheetForm.name}>
              {sheetLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><CheckCircle className="w-4 h-4 mr-2" />Save Changes</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Mapping Configuration Dialog ---- */}
      <Dialog open={showMappingDialog} onOpenChange={setShowMappingDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-indigo-600" />
              Field Mapping Configuration
            </DialogTitle>
            <DialogDescription>Map Google Sheet columns to CRM lead fields. New custom fields appear automatically.</DialogDescription>
          </DialogHeader>
          {mappingIntegrationId && (
            <MappingPanel integrationId={mappingIntegrationId} onMappingSaved={() => { onRefresh(); }} />
          )}
        </DialogContent>
      </Dialog>

      {/* ---- Logs Dialog ---- */}
      <Dialog open={showSheetLogsDialog} onOpenChange={setShowSheetLogsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sync Logs - {selectedSheetIntegration?.name}</DialogTitle>
            <DialogDescription>View sync history and errors</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {sheetLogs.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No sync logs yet</p>
            ) : sheetLogs.map(log => (
              <div key={log.log_id} className={`p-3 rounded-lg border ${
                log.status === "success" ? "bg-green-50 border-green-200" :
                log.status === "partial" ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200"
              }`}>
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    {log.status === "success" ? <CheckCircle className="w-4 h-4 text-green-600" /> :
                     log.status === "partial" ? <AlertTriangle className="w-4 h-4 text-amber-600" /> :
                     <XCircle className="w-4 h-4 text-red-600" />}
                    <span className="font-medium capitalize">{log.status}</span>
                  </div>
                  <span className="text-xs text-slate-500">{new Date(log.created_at).toLocaleString()}</span>
                </div>
                {log.leads_created !== undefined && (
                  <div className="mt-2 text-sm text-slate-600 grid grid-cols-3 gap-2">
                    <span>Created: <strong>{log.leads_created}</strong></span>
                    <span>Updated: <strong>{log.leads_updated}</strong></span>
                    <span>Skipped: <strong>{log.leads_skipped}</strong></span>
                  </div>
                )}
                {log.error && <p className="mt-2 text-sm text-red-700">{log.error}</p>}
                {log.errors && log.errors.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-slate-500">Errors:</p>
                    <ul className="text-xs text-red-600 list-disc list-inside">
                      {log.errors.slice(0, 5).map((err, i) => <li key={i}>{err}</li>)}
                      {log.errors.length > 5 && <li>...and {log.errors.length - 5} more</li>}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter><Button onClick={() => setShowSheetLogsDialog(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
